import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Form, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, Resolver, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FlatService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { IFlat } from "../../shared/interfaces/flat/IFlat";
import { IPhoto } from "../../shared/interfaces/shared/IBase";
import { formatDate } from "../../shared/functions/formatDate";
import { FloorService } from "../floors/service";
import DeleteModal from "../../Common/DeleteModal";
import { ILocation } from "../../shared/interfaces/location/ILocation";
import { LocationService } from "../locations/service";
import { IFloor } from "../../shared/interfaces/floor/IFloor";
import LocalizedFileInput from "../../shared/LocalizedFileInput";
import { useUnsavedChanges } from "../../shared/hooks/useUnsavedChanges";

const flatSchema = yup.object({
  name: yup.string().required("validation.required"),
  floorId: yup.number().required("validation.required"),
  locationId: yup.number().required("validation.required"),
  height: yup.number().transform((value) => (isNaN(value) ? undefined : value)).optional(),
  elevator: yup.boolean().optional(),
  id: yup.number().optional(),
  images: yup.array().default([]),
  documents: yup.array().default([]),
});

const FlatForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [floors, setFloors] = useState<{ value: number; label: string }[]>([]);
  const [locations, setLocations] = useState<
    { value: number; label: string }[]
  >([]);
  const [selectedFile, setSelectedFile] = useState<IPhoto | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<IFlat>({
    defaultValues: !!id
      ? async () => {
        try {
          const data = await FlatService.getSingle(Number(id));
          return {
            ...data,
            floorId: data?.floor?.id,
            locationId: data?.location?.id,
          };
        } catch (e) {
          ErrorToast(e);
        }
      }
      : {
        name: "",
        floorId: undefined,
        locationId: undefined,
        height: undefined,
        elevator: false,
        images: [],
        documents: [],
        id: undefined,
      },
    resolver: yupResolver(flatSchema) as unknown as Resolver<IFlat>,
  });

  const { guardedNavigate, unsavedChangesModal } = useUnsavedChanges(isDirty);

  const { files } = useWatch({
    control,
  });

  const handleSelectFile = (file: IPhoto) => {
    setSelectedFile(file);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setSelectedFile(null);
    setShowDeleteModal(false);
  };

  const deleteFile = () => {
    !!selectedFile?.id &&
      FlatService.deleteFile(selectedFile?.id)
        .then((response) => {
          if (response) {
            SuccessToast(t("file.successDelete"));
            handleCloseDeleteModal();
            setValue(
              "files",
              (watch("files") ?? []).filter(
                (file) => file.id !== selectedFile?.id,
              ),
            );
          }
        })
        .catch(ErrorToast);
  };

  const onSubmit = async (flat: IFlat) => {
    const isUpdate = !!flat?.id;
    const formData = new FormData();
    formData.append(
      "body",
      JSON.stringify({
        ...flat,
        documents: undefined,
        images: undefined,
      }),
    );
    for (const document of flat?.documents ?? []) {
      formData.append("documents", document);
    }
    for (const image of flat?.images ?? []) {
      formData.append("images", image);
    }
    const CorrectService = isUpdate ? FlatService.update : FlatService.create;
    CorrectService(formData, flat?.id)
      .then(() => {
        SuccessToast(
          t(
            isUpdate
              ? "toasts.successfullyUpdated"
              : "toasts.successfullyCreated",
            {
              title: flat?.name,
            },
          ),
        );
        navigate("/flats");
      })
      .catch(ErrorToast);
  };

  useEffect(() => {
    FloorService.getAll({ page: 1, perPage: 25 })
      .then((response) => {
        setFloors(
          response?.data?.map((floor: IFloor) => ({
            value: floor?.id,
            label: floor?.name,
          })),
        );
      })
      .catch(ErrorToast);
  }, []);

  useEffect(() => {
    LocationService.getAll({ page: 1, perPage: 100 })
      .then((response) => {
        setLocations(
          response?.data?.map((location: ILocation) => ({
            value: location?.id,
            label: `${location?.address}, ${location?.city}`,
          })),
        );
      })
      .catch(ErrorToast);
  }, []);
  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="g-3">
                  <Col xl={4}>
                    <Form.Group>
                      <Form.Label className={"required"}>
                        {t("flat.flatName")}
                      </Form.Label>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field, fieldState }) => (
                          <>
                            <Form.Control
                              type="text"
                              isInvalid={!!fieldState.error}
                              placeholder={t("flat.enterFlatName")}
                              {...field}
                            />
                            <Form.Control.Feedback type="invalid">
                              {t(fieldState.error?.message as string)}
                            </Form.Control.Feedback>
                          </>
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col xl={3}>
                    <Form.Group>
                      <Form.Label className={"required"}>
                        {t("flat.floor")}
                      </Form.Label>
                      <Controller
                        name="floorId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            classNamePrefix="react-select"
                            className={`react-select-container ${errors.floorId ? "is-invalid" : ""}`}
                            options={floors}
                            value={
                              floors.find(
                                (option) => option.value === field.value,
                              ) || null
                            }
                            onChange={(option) =>
                              field.onChange(option ? option.value : undefined)
                            }
                            placeholder={t("flat.selectFloor")}
                            isClearable
                          />
                        )}
                      />
                      {errors.floorId && (
                        <Form.Control.Feedback type="invalid">
                          {t("validation.required")}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col xl={3}>
                    <Form.Group>
                      <Form.Label className={"required"}>
                        {t("customer.locations")}
                      </Form.Label>
                      <Controller
                        name="locationId"
                        control={control}
                        render={({ field }) => (
                          <Select
                            classNamePrefix="react-select"
                            className={`react-select-container ${errors.locationId ? "is-invalid" : ""}`}
                            options={locations}
                            value={
                              locations.find(
                                (option) => option.value === field.value,
                              ) || null
                            }
                            onChange={(option) =>
                              field.onChange(option ? option.value : undefined)
                            }
                            placeholder={t("job.selectLocation")}
                            isClearable
                          />
                        )}
                      />
                      {errors.locationId && (
                        <Form.Control.Feedback type="invalid">
                          {t("validation.required")}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col xl={2}>
                    <Form.Group controlId="height">
                      <Form.Label>{t("flat.height")}</Form.Label>
                      <Controller
                        name="height"
                        control={control}
                        render={({ field }) => (
                          <Form.Control
                            type="number"
                            step={0.01}
                            placeholder={t("flat.enterHeight")}
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value),
                              )
                            }
                          />
                        )}
                      />
                    </Form.Group>
                  </Col>
                  <Col xl={2}>
                    <Form.Group className="mb-3" controlId="elevator">
                      <Form.Label>{t("flat.elevator")}</Form.Label>
                      <Form.Check className="mt-2" type="checkbox" />
                    </Form.Group>
                  </Col>
                  <Col xl={12}>
                    <Form.Group controlId="note">
                      <Form.Label>{t("general.note")}</Form.Label>
                      <Controller
                        control={control}
                        name="note"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <Form.Control
                            as="textarea"
                            rows={4}
                            onChange={onChange}
                            value={value ?? ""}
                            isInvalid={!!errors?.note?.message?.length}
                            placeholder={t("general.enterNote")}
                          />
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(errors.note?.message as string)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group controlId="documents">
                      <Form.Label>{t("flat.files")} </Form.Label>
                      <Controller
                        name={`documents`}
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <>
                            <LocalizedFileInput
                              accept={"application/*"}
                              multiple
                              value={value as File[] | null | undefined}
                              onChange={(files) => onChange(files)}
                            />
                            <Form.Control.Feedback type="invalid">
                              {t(errors?.documents?.message as string)}
                            </Form.Control.Feedback>
                          </>
                        )}
                      />
                    </Form.Group>
                  </Col>
                  {(files as IPhoto[])
                    ?.filter((file) => file.type === "DOCUMENT_TYPE")
                    ?.map((document) => {
                      return (
                        <Col
                          md={4}
                          className={"text-center mb-3"}
                          key={document?.id}
                        >
                          <Card className="custom-card cursor-pointer">
                            <Card.Body>
                              <div>
                                <i className="ti ti-document f-20"></i>
                                <p className="mt-1">
                                  File: {document?.file?.nameOriginal}
                                </p>
                                <span className="text-muted">
                                  {formatDate(document?.createdAt)}
                                </span>
                              </div>
                              <button
                                onClick={() => handleSelectFile(document)}
                                type={"button"}
                                className={"btn btn-icon btn-sm delete-icon"}
                              >
                                <i className="ti ti-trash f-20"></i>
                              </button>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    })}
                  <Col md={12}>
                    <Form.Group className="mb-3" controlId="images">
                      <Form.Label>{t("flat.photos")} </Form.Label>
                      <Controller
                        name={`images`}
                        control={control}
                        render={({ field: { onChange } }) => (
                          <>
                            <LocalizedFileInput
                              accept={"image/*"}
                              multiple
                              value={undefined}
                              onChange={(files) => onChange(files)}
                            />
                            <Form.Control.Feedback type="invalid">
                              {t(errors?.images?.message as string)}
                            </Form.Control.Feedback>
                          </>
                        )}
                      />
                    </Form.Group>
                  </Col>
                  {(files as IPhoto[])
                    ?.filter((file) => file.type === "IMAGE_TYPE")
                    ?.map((photo) => {
                      return (
                        <Col
                          md={3}
                          className={"text-center mb-3"}
                          key={photo?.id}
                        >
                          <div className={"position-relative"}>
                            <img
                              className={"gallery-image w-100"}
                              src={photo?.path}
                              alt={photo?.file?.nameOriginal}
                            />
                            <button
                              onClick={() => handleSelectFile(photo)}
                              type={"button"}
                              className={"btn btn-icon btn-sm delete-icon"}
                            >
                              <i className="ti ti-trash f-20"></i>
                            </button>
                          </div>
                        </Col>
                      );
                    })}
                  <Col sm={12} className="d-flex gap-2">
                    <Button type="submit" variant="primary">
                      {t(`flat.${isEdit ? "update" : "create"}`)}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => guardedNavigate("/flats")}>
                      {t("button.cancel")}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <DeleteModal
        show={showDeleteModal}
        name={selectedFile?.file?.nameOriginal}
        handleClose={handleCloseDeleteModal}
        handleDeleteId={deleteFile}
      />
      {unsavedChangesModal}
    </React.Fragment>
  );
};

export default FlatForm;
